import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';

import { Link } from 'inside-out/components/shared/link';
import { ArrowLink } from 'inside-out/components/shared/arrowLink';
import { Cards } from 'inside-out/components/shared/cards';
import { ResourceCard } from 'inside-out/components/shared/cards/resourceCard';
import { RememberPrompt } from 'inside-out/components/shared/rememberPrompt';
import BackArrowIcon from 'inside-out/components/shared/icons/backArrowIcon';

import { AuthManager } from 'inside-out/managers/authManager';
import { gradeScreener } from 'inside-out/managers/screenerManager';
import { descriptionToHex } from 'inside-out/managers/colourManager';
import { idToLink } from 'inside-out/managers/navManager';
import { extractAudiences } from 'inside-out/managers/resourceLibraryManager';

import { Queries } from './queries';

import { contentSection, button, aqua, cardSection, headingMedium } from 'inside-out/global-styles';

import {
	scoreStyle,
	titleStyle,
	wrapperStyle,
	wrapperResultsStyle,
	innerWrapperStyle,
	introTextStyle,
	progressStyle,
	bodyTextStyle,
	answersWrapStyle,
	answerStyle,
	answerActiveStyle,
	additionalStyleWrapper,
	additionalStyle,
	resultTitleStyle,
	compressedStyle,
	boolStyle,
	backButtonWrapperStyle,
	backButtonInnerWrapperStyle,
	backButtonStyle,
} from './styles';

export class ScreenerAnswers extends React.PureComponent {
	static variants = {
		compressed: compressedStyle,
		bool: boolStyle,
	};

	static getWrapper = () => {
		return answersWrapStyle;
	};

	render() {
		const { currentAnswer, answers, onClick, variantWrapperStyle } = this.props;
		return (
			<div className={variantWrapperStyle}>
				{answers.map((answer, index) => (
					<button
						key={index}
						className={
							currentAnswer === answer.value
								? answerActiveStyle
								: answerStyle(answers.length - index)
						}
						onClick={onClick.bind(this, answer)}
					>
						{answer.name}
					</button>
				))}
			</div>
		);
	}
}

ScreenerAnswers.propTypes = {
	answers: PropTypes.array,
	onClick: PropTypes.func,
	variantWrapperStyle: PropTypes.any,
	currentAnswer: PropTypes.any,
};

ScreenerAnswers.defaultProps = {
	variantWrapperStyle: ScreenerAnswers.getWrapper(),
};

export class Screener extends React.PureComponent {
	static propTypes = {
		location: PropTypes.shape({
			search: PropTypes.string.isRequired,
		}).isRequired,

		type: PropTypes.oneOf(['Consumer', 'Carer']).isRequired,

		resultTiles: PropTypes.shape({
			left_heading: PropTypes.string,
			left_body: PropTypes.string,
			left_link_text: PropTypes.string,
			left_link_url: PropTypes.string,
			right_heading: PropTypes.string,
			right_body: PropTypes.string,
			right_link_text: PropTypes.string,
			right_link_url: PropTypes.string,
		}).isRequired,

		pages: PropTypes.arrayOf(
			PropTypes.shape({
				question: PropTypes.string.isRequired,
				description: PropTypes.string.isRequired,
				answers: PropTypes.arrayOf(
					PropTypes.shape({
						name: PropTypes.string.isRequired,
						value: PropTypes.string.isRequired,
					})
				),
			})
		),

		resultPages: PropTypes.object.isRequired,
		headerManager: PropTypes.any,
		depressionContent: PropTypes.shape({
			heading: PropTypes.string,
			body: PropTypes.string,
		}),
	};

	state = {
		answers: [],
		pageIndex: 0,
	};

	componentWillMount = () => {
		const { location, pages } = this.props;
		const query = qs.parse(location.search, { ignoreQueryPrefix: true });

		// If we have an attempt to set the value, check that we can
		if (query && query.first) {
			const desiredAnswer = pages[0].answers.find(answer => answer.value === query.first);

			// And if it's a valid answer, skip the page, take the value.
			if (desiredAnswer) {
				this.setState({
					answers: [parseInt(desiredAnswer.value)],
					pageIndex: 1,
				});
			}
		}
	};

	recordAnswer = (value, userResult, recordResults) => {
		const { pages, resultPages, type, depressionContent } = this.props;
		const { pageIndex } = this.state;

		const answers = JSON.parse(JSON.stringify(this.state.answers));
		answers[pageIndex] = parseInt(value);

		let result = undefined;
		let resultContent = undefined;
		let resultColour = undefined;

		if (answers.length === pages.length) {
			result = gradeScreener({
				type,
				pages,
				answers,
			});

			resultContent = resultPages[result.rating];

			switch (result.rating) {
				case 'Low':
					resultColour = 'blue';
					break;
				case 'Medium':
					resultColour = 'orange';
					break;
				case 'High':
					resultColour = 'burned-orange';
					break;
			}

			if (result.depressed) {
				if (depressionContent.heading) {
					resultContent.heading += depressionContent.heading;
				}
				if (depressionContent.body) {
					resultContent.body += depressionContent.body;
				}
			}

			// Save
			// Get the data in the right shape first.
			const serverAnswers = answers.map((answer, index) => {
				const fullAnswer = pages[index].answers[answer];

				return {
					questionIndex: index,
					questionText: pages[index].question,
					text: fullAnswer.name,
					value: parseInt(fullAnswer.value),
				};
			});

      // asynchronously upload the screener.
      const uploadResults = async () => {
			  const variables = {
          type,
			  	answers: serverAnswers,
			  	rating: result.rating,
          token: await AuthManager.sharedInstance.getQuestionnaireToken()
			  };
        // if we're not logged in, create a token that links the two questionnaires
        // and that we can retrieve later on login/register.
        recordResults({ variables }); 

        // if we're logged in, link it now
			  const userId = userResult.user && userResult.user.id;
        if (userId) {
          await AuthManager.sharedInstance.saveQuestionnaireResults();
        }
      };
      uploadResults();
		}

		this.setState({
			answers,
			pageIndex: Math.min(this.state.pageIndex + 1, pages.length),
			result,
			resultContent,
			resultColour,
		});
	};

	previousQuestion = () => {
		const { pageIndex } = this.state;
		if (pageIndex === 0) return;
		this.setState({
			pageIndex: Math.max(pageIndex - 1, 0)
		});
	};

	render() {
		const { resultTiles, headerManager } = this.props;
		const { pageIndex, answers, result, resultContent, resultColour } = this.state;
		const currentAnswer = `${ answers[pageIndex] }`;
		const page = this.props.pages[pageIndex];
		const progress = pageIndex / this.props.pages.length * 100;

		const isLoggedIn = AuthManager.sharedInstance.isLoggedIn;

		if (result && headerManager) {
			headerManager.headerProperties = {
				...headerManager.headerProperties,
				cta: !isLoggedIn
					? {
							question: 'Save these results to your profile',
							callToAction: {
								to: '/register',
								name: 'Create an InsideOut Profile',
							},
					  }
					: null,
			};
		}

		const resources = resultContent
			? [
					resultContent.resource_1,
					resultContent.resource_2,
					resultContent.resource_3,
					resultContent.resource_4,
			  ]
			: null;

		return (
			<React.Fragment>
				<div className={progressStyle(progress)} />

				{result && (
					<div className={!isLoggedIn ? wrapperResultsStyle : null}>
						<div className={contentSection('#fff')}>
							<section>
								<div className={innerWrapperStyle}>
									<h1 className={scoreStyle}>Your score</h1>
									<div
										className={resultTitleStyle(descriptionToHex(resultColour))}
										dangerouslySetInnerHTML={{
											__html: resultContent.heading,
										}}
									/>
									<div
										className={bodyTextStyle}
										dangerouslySetInnerHTML={{
											__html: resultContent.body,
										}}
									/>
									<Link className={button(aqua)} to={resultContent.button_url}>
										{resultContent.button_text}
									</Link>

									{!isLoggedIn && <RememberPrompt />}
								</div>

								<div className={additionalStyleWrapper}>
									<div className={innerWrapperStyle}>
										<div className={additionalStyle}>
											<div>
												<h2>{resultTiles.left_heading}</h2>
												<div
													dangerouslySetInnerHTML={{
														__html: resultTiles.left_body,
													}}
												/>
												<ArrowLink to={resultTiles.left_link_url}>
													{resultTiles.left_link_text}
												</ArrowLink>
											</div>
											<div>
												<h2>{resultTiles.right_heading}</h2>
												<div
													dangerouslySetInnerHTML={{
														__html: resultTiles.right_body,
													}}
												/>
												<ArrowLink to={resultTiles.right_link_url}>
													{resultTiles.right_link_text}
												</ArrowLink>
											</div>
										</div>
									</div>
								</div>
							</section>

							<section>
								<div className={innerWrapperStyle}>
									<div className={cardSection(60)}>
										<header>
											<h1 className={headingMedium}>Resources</h1>
											<ArrowLink to="/resource-library">View all</ArrowLink>
										</header>
										<Cards columns={4}>
											{resources.map((node, index) => (
												<ResourceCard
													id={node.id}
													key={index}
													to={idToLink(node.id)}
													background={
														node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
													}
													audiences={extractAudiences(node.categories)}
													hasVideo={!!node.attachments.find(a => a.vimeo_url)}
													title={node.title}
													body={node.extract}
												/>
											))}
										</Cards>
									</div>
								</div>
							</section>
						</div>
					</div>
				)}

				{!result && (
					<Queries>
						{({ loading, data, recordResults }) =>
							!loading && (
								<div className={wrapperStyle}>
									{ pageIndex > 0 && (
										<div className={ backButtonWrapperStyle }>
											<div className={ backButtonInnerWrapperStyle }>
												<div className={ backButtonStyle } onClick={ this.previousQuestion }>
													<BackArrowIcon fill="inherit" /> Back
												</div>
											</div>
										</div>
									) }
									<div className={contentSection('#fff')}>
										<section className={innerWrapperStyle}>
											<div className={introTextStyle}>
												<h1 className={titleStyle}>{page.question}</h1>
												<div
													className={bodyTextStyle}
													dangerouslySetInnerHTML={{
														__html: page.description,
													}}
												/>
												<ScreenerAnswers
													currentAnswer={currentAnswer}
													answers={page.answers}
													onClick={answer => this.recordAnswer(answer.value, data, recordResults)}
												/>
											</div>
										</section>
									</div>
								</div>
							)
						}
					</Queries>
				)}
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/shared/screener/component.js